<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-alert
        variant="danger"
        :show="alertErrors.length > 0"
        class="mb-0"
      >
        <div
          v-for="(row, index) in alertErrors"
          :key="index"
          class="alert-body"
        >
          <feather-icon

            icon="InfoIcon"
            class="mr-50"
          />
          {{ row }}
        </div>
      </b-alert>
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_OPTED_OUT_NEWSLETTER') }}</h4></b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-form-checkbox
                  v-model="useUserData.opted_out_newsletter"
                  checked="true"
                  class="custom-control-primary"
                  name="check-button"
                  switch
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="CheckIcon" />
                  </span>
                  <span class="switch-icon-right">
                    <feather-icon icon="XIcon" />
                  </span>
                </b-form-checkbox>
              </b-col>
            </b-row>
            <hr>
            <b-row>

              <!-- Field: Username -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Username"
                  data-vv-validate-on="blur"
                  rules="required|alpha-num"
                >
                  <b-form-group

                    :label="$t('DEBTIA_USERNAME')"
                    label-for="username"
                  >
                    <b-form-input
                      id="username"
                      v-model="useUserData.username"
                      :state="getValidationState(validationContext)"
                      trim
                      :maxlength="maxCharactersAllowed"
                      @input="characterLeft"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                    <div>
                      <small
                        :style="maxCharacters > 10 ? 'color: green' : 'color: red'"
                      >
                        {{ maxCharacters }} characters left out of {{ maxCharactersAllowed }}
                      </small>
                    </div>
                    <span
                      v-if="!validateUsername"
                      style="color:#ea5455"
                    >
                      Username is not valid
                    </span>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="useUserData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="useUserData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PHONE')"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="useUserData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Status -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_LOGIN')"
                  label-for="login-status"
                >
                  <v-select
                    v-model="useUserData.loginStatus"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="loginOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="login-status"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Billing Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Billing Email"
                  rules="email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_BILLING_EMAIL')"
                    label-for="billing-email"
                  >
                    <b-form-input
                      id="billing-email"
                      v-model="useUserData.billing_email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Contact Person -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CONTACT_PERSON')"
                  label-for="contact-person"
                >
                  <b-form-input
                    id="contact-person"
                    v-model="useUserData.contact_person"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Type -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CLIENT_TYPE')"
                  label-for="client-type"
                >
                  <v-select
                    v-model="useUserData.type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="client-type"
                    :value="'person'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: CVRCPR -->
              <b-col
                v-if="useUserData.type === 'capital-company' || useUserData.type === 'sole-proprietorship'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="CVR"
                  rules="integer|length:8"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CVRCPR')"
                    label-for="cvrcpr"
                  >
                    <b-form-input
                      id="cvrcpr"
                      v-model="useUserData.cvrcpr"
                      :state="getValidationState(validationContext)"
                      trim
                      maxlength="8"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

              </b-col>

              <!-- Field: CPR (Optional Field) -->
              <b-col
                v-if="useUserData.type === 'person'"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CPR')"
                  label-for="cpr"
                >
                  <cleave
                    id="cpr"
                    v-model="useUserData.cpr"
                    class="form-control"
                    :raw="false"
                    :options="options.cprDelimiter"
                  />
                </b-form-group>

              </b-col>

              <!-- Field: TERMSHEETCODE -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  class="classformgroup"
                  :label="$t('DEBTIA_TERMSHEETCODE')"
                  label-for="debtor-login-status"
                >
                  <v-select
                    v-model="useUserData.termSheetCode"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="termSheetCodeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="debtor-login-status"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group :label="$t('DEBTIA_COLLECTION_TYPE')">
                  <b-form-radio-group
                    v-model="useUserData.collection_type"
                    :options="collectiontypeoptions"
                    class="demo-inline-spacing"
                    name="radio-inline"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  class="classformgroup"
                  :label="$t('DEBTIA_MONITORING_TERMSHEETCODE')"
                  label-for="monitoring_termsheetcode"
                >
                  <v-select
                    v-model="useUserData.monitoring_termsheetcode"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="[{label: 'Select Monitoring Termsheet', value: null}, ...termSheetCodeOptions]"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="monitoring_termsheetcode"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="RKI Customer Number"
                  rules="integer"
                >
                  <b-form-group
                    :label="$t('DEBTIA_RKI_CUSTOMER_NUMBER')"
                    label-for="rki-customer-number"
                  >
                    <b-form-input
                      id="rki-customer-id"
                      v-model="useUserData.rki_customer_id"
                      :state="getValidationState(validationContext)"
                      trim
                      maxlength="10"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

              </b-col>
            </b-row>

            <hr v-if="useUserData.type !== 'person'">

            <b-row v-if="useUserData.type === 'sole-proprietorship' || useUserData.type === 'capital-company'">
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_VAT_EXEMPTION') }}</h4></b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-form-checkbox
                  v-model="useUserData.vat_exempted"
                  checked="true"
                  class="custom-control-primary"
                  name="check-button"
                  switch
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="CheckIcon" />
                  </span>
                  <span class="switch-icon-right">
                    <feather-icon icon="XIcon" />
                  </span>
                </b-form-checkbox>
              </b-col>
            </b-row>
            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_BANK_INFORMATION') }}</h4></b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_BRANCH_NO')"
                  label-for="bankBranchNo"
                >
                  <b-form-input
                    id="bankBranchNo"
                    v-model="useUserData.bankBranchNo"
                    trim
                    disabled
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_ACCOUNT_NO')"
                  label-for="bankAccountNo"
                >
                  <b-form-input
                    id="bankAccountNo"
                    v-model="useUserData.bankAccountNo"
                    trim
                    disabled
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_IBAN')"
                  label-for="iban"
                >
                  <b-form-input
                    id="iban"
                    v-model="useUserData.iban"
                    trim
                    disabled
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_SWIFTCODE')"
                  label-for="swiftCode"
                >
                  <b-form-input
                    id="swiftCode"
                    v-model="useUserData.swiftCode"
                    trim
                    disabled
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="house-number"
                      v-model="useUserData.address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="useUserData.address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="useUserData.address.place"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <b-form-input
                      id="zip-code"
                      v-model="useUserData.address.zip_code"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getCityName('standard_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CITY')"
                    label-for="city"
                  >
                    <b-form-input
                      id="city"
                      v-model="useUserData.address.city"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getZipCode('standard_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  v-slot="{ errors, validated, failed }"
                  name="Country"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COUNTRY')"
                    label-for="select-country"
                    :state="failed ? false : (validated ? true : null)"
                  >
                    <v-select
                      id="select-country"
                      v-model="useUserData.address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="true"
                      placeholder="Select Country"
                      :class="{ 'is-invalid': errors.length > 0 }"
                    >
                      <template #selected-option-container="{ option }">
                        <div class="vs__selected">
                          {{ $t(option.label) }}
                        </div>
                      </template>

                      <template #option="{ label }">
                        {{ $t(label) }}
                      </template>
                    </v-select>

                    <b-form-invalid-feedback v-if="errors[0]">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_BILLING_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Billing House Number"
                  :rules="addressRequired() ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="billing-house-number"
                  >
                    <b-form-input
                      id="billing-house-number"
                      v-model="useUserData.billing_address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Billing Road Name"
                  :rules="addressRequired() ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="billing-road-name"
                  >
                    <b-form-input
                      id="billing-road-name"
                      v-model="useUserData.billing_address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Billing Place"
                  :rules="addressRequired() ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PLACE_NAME')"
                    label-for="billing-place"
                  >
                    <b-form-input
                      id="billing-place"
                      v-model="useUserData.billing_address.place"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Billing Zip Code"
                  :rules="addressRequired() ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="billing-zip-code"
                  >
                    <b-form-input
                      id="billing-zip-code"
                      v-model="useUserData.billing_address.zip_code"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getCityName('billing_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Billing City"
                  :rules="addressRequired() ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CITY')"
                    label-for="billing-city"
                  >
                    <b-form-input
                      id="billing-city"
                      v-model="useUserData.billing_address.city"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getZipCode('billing_address', $event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_COUNTRY')"
                  label-for="billing-country"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Billing Country"
                    :rules="addressRequired() ? 'required' : ''"
                  >
                    <v-select
                      v-model="useUserData.billing_address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="false"
                      input-id="select-country"
                      :value="'Select Country'"
                    >
                      <template
                        #selected-option-container="{ option }"
                      >
                        <div class="vs__selected">
                          {{ $t(option.label) }}</div>
                      </template>
                      <template
                        slot="option"
                        slot-scope="option"
                      >
                        {{ $t(option.label) }}
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

            </b-row>

            <hr>

            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              Save Changes
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              Reset
            </b-button>
          </b-form>

        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput, BForm, BCard, BFormInvalidFeedback, BOverlay, BAlert, BFormCheckbox, BFormRadioGroup,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import { ref/* , onUnmounted  */ } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'
import Cleave from 'vue-cleave-component'
import i18n from '@/libs/i18n'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'
import countries from '@/countries'

import formValidation from '@core/comp-functions/forms/form-validation'
import { required, alphaNum, email } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import clientStoreModule from '../clientStoreModule'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    BOverlay,
    BAlert,
    BFormCheckbox,
    Cleave,
    BFormRadioGroup,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      busy: false,
      countries,
      required,
      alphaNum,
      email,
      alertErrors: [],
      useUserData: {
        ...this.userData,
        address: this.userData.address || {},
        billing_address: this.userData.billing_address || {},
      },
      validateUsername: true,
      debtCaseId: 0,
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
      },
      termSheetCodeOptions: [],
      termSheetCodeLoader: false,
      maxCharacters: 50,
      maxCharactersAllowed: 50,
      // collectiontypeoptions: [
      //   { text: i18n.t('DEBTIA_FOREIGN'), value: 'foreign' },
      //   { text: i18n.t('DEBTIA_OWN'), value: 'own' },
      // ],
    }
  },
  setup(props) {
    const loginOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]
    const loader = false

    const refInputEl = ref(null)
    const previewEl = ref(null)

    const {
      refFormObserver,
      getValidationState,
    } = formValidation(props.userData)

    const typeOptions = [
      { label: 'Person', value: 'person' },
      { label: 'Sole Proprietorship', value: 'sole-proprietorship' },
      { label: 'Capital Company', value: 'capital-company' },
    ]

    return {
      avatarText,
      loginOptions,
      loader,
      refInputEl,
      previewEl,
      refFormObserver,
      getValidationState,
      BFormInvalidFeedback,
      typeOptions,
    }
  },
  computed: {
    collectiontypeoptions() {
      const temp = [
        { text: i18n.t('DEBTIA_FOREIGN'), value: 'foreign' },
        { text: i18n.t('DEBTIA_OWN'), value: 'own' },
      ]

      return temp
    },
  },
  created() {
    this.debtCaseId = this.$route.query.debt_case_id
    this.getTermsheetCodes()
    this.maxCharacters = this.maxCharactersAllowed - this.useUserData.username.length
    this.useUserData.opted_out_newsletter = !!Number(this.useUserData.opted_out_newsletter)
  },
  methods: {
    addressRequired() {
      if (this.userData.billing_address.road_name
            || this.userData.billing_address.house_number
              || this.userData.billing_address.place
                || this.userData.billing_address.zip_code
                  || this.userData.billing_address.city
                    || this.userData.billing_address.country_id) {
        return true
      }
      return false
    },
    getCityName(type, event) {
      axios.get(`settings/get-city-name-by-code?code=${event}`)
        .then(response => {
          if (type === 'standard_address') {
            if (response.data.data.city_name !== null) {
              this.useUserData.address.city = response.data.data.city_name
            }
          }
          if (type === 'billing_address') {
            if (response.data.data.city_name !== null) {
              this.useUserData.billing_address.city = response.data.data.city_name
            }
          }
        }).catch(error => {
          console.log(error)
        })
    },
    getZipCode(type, event) {
      axios.get(`settings/get-zip-code-by-city-name?city=${event}`)
        .then(response => {
          if (type === 'standard_address') {
            if (response.data.data.zip_code !== null) {
              this.useUserData.address.zip_code = response.data.data.zip_code
            }
          }
          if (type === 'billing_address') {
            if (response.data.data.zip_code !== null) {
              this.useUserData.billing_address.zip_code = response.data.data.zip_code
            }
          }
        }).catch(error => {
          console.log(error)
        })
    },
    getTermsheetCodes() {
      this.termSheetCodeLoader = true
      axios.get('/settings/termsheetcode-dropdown').then(response => {
        if (response.status === 200) {
          const termsheetCodes = response.data.data
          this.termSheetCodeOptions = termsheetCodes
          this.termSheetCodeLoader = false
        }
      })
    },
    EditClientPayload() {
      const user = this.useUserData
      const address = user.address || {}
      const billingAddress = user.billing_address || {}

      return {
        id: user.id,
        bc_customer_id: user.bc_customer_id,
        username: user.username,
        name: user.name,
        email: user.email,
        phone: user.phone,
        loginStatus: user.loginStatus,
        type: user.type,
        company_type: user.company_type,
        cvrcpr: user.cvrcpr,
        cpr: user.cpr,
        birth_date: user.birth_date,
        billing_email: user.billing_email,
        contact_person: user.contact_person,
        termSheetCode: user.termSheetCode,
        monitoring_termsheetcode: user.monitoring_termsheetcode,
        collection_type: user.collection_type,
        rki_customer_id: user.rki_customer_id,
        vat_exempted: user.vat_exempted,
        opted_out_newsletter: user.opted_out_newsletter,
        bankBranchNo: user.bankBranchNo,
        bankAccountNo: user.bankAccountNo,
        iban: user.iban,
        swiftCode: user.swiftCode,
        address: {
          road_name: address.road_name,
          house_number: address.house_number,
          place: address.place,
          zip_code: address.zip_code,
          city: address.city,
          country_id: address.country_id,
        },
        billing_address: {
          road_name: billingAddress.road_name,
          house_number: billingAddress.house_number,
          place: billingAddress.place,
          zip_code: billingAddress.zip_code,
          city: billingAddress.city,
          country_id: billingAddress.country_id,
        },
      }
    },
    async onSubmit() {
      this.loader = true
      const data = this.EditClientPayload()
      const validation = await axios.post('clients/validate-username', { username: data.username, exclude: [data.id] })
        .then(() => true).catch(() => false)

      if (!validation) {
        this.validateUsername = validation
        this.loader = false
        return false
      }
      const USER_APP_STORE_MODULE_NAME = 'app-client'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, clientStoreModule)
      data.debt_case_id = this.debtCaseId
      // console.log('--->', data)
      store.dispatch('app-client/editClient', data)
        .then(() => {
          // console.log('done', response)
          this.$router.replace(`/clients/view/${data.id}`)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Client Updated',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Client successfully Updated!',
                },
              })
            })
        })
        .catch(error => {
          this.alertErrors = []
          this.alertErrors.push(...error.data.message)
          // console.log('error**', this.alertErrors)
          this.loader = false
        })
      return true
    },

    resetForm() {
      this.useUserData = { ...this.userData }
    },
    characterLeft(event) {
      this.maxCharacters = this.maxCharactersAllowed - event.length
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.spinner-border{
  width: 6rem;
  height: 6rem;
}

.custom-control.custom-control-inline.custom-radio {
  margin-top: 5px;
}
</style>
